// frontend-react/src/pages/adReview/useAdReviewController.ts
// ✅ 集中管理：AdReviewPage 所有 state + handler（API 呼叫、進度條模擬、歷史/保存/下載、規則集）

import { createElement, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { message, Modal, Upload } from 'antd';
import type { UploadProps } from 'antd';

import { useAuth } from '../../contexts/AuthContext';
import { useFeatureFlags } from '../../hooks/useFeatureFlags';
import { authService } from '../../services/authService';
import apiClient from '../../services/apiClient';
import { saveReviewHistory, type ReviewHistoryDetail } from '../../services/reviewHistoryService';
import {
  loadRuleSets as loadRuleSetsService,
  loadManageRuleSets,
  setUserPreferredRule,
  loadRuleSetDetail,
} from '../../services/ruleService';

import { API_ENDPOINTS, getApiBaseUrl } from '../../config/api';
import type { ReviewItem } from '../../types';
import type { RuleSet } from '../../types/rule';

import { calculateRiskCounts, fileToBase64 } from './adReviewUtils';
import {
  requestNotificationPermission,
  notifyReviewComplete,
} from '../../utils/notifyReviewComplete';

type TestData = { fileName: string; timestamp: number };

export const useAdReviewController = () => {
  const { user, refreshUser } = useAuth();
  const location = useLocation();

  // -----------------------------
  // 基本狀態
  // -----------------------------
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [pdfData, setPdfData] = useState<ArrayBuffer | null>(null);
  const [reviewResults, setReviewResults] = useState<ReviewItem[]>([]);
  const [deletedItemIds, setDeletedItemIds] = useState<Set<string>>(new Set());
  const [editedItems, setEditedItems] = useState<Map<string, Partial<ReviewItem>>>(new Map());
  const [isReviewing, setIsReviewing] = useState(false);

  // 歷史/測試模式
  const [historyVisible, setHistoryVisible] = useState(false);
  const [testMode, setTestMode] = useState(false);
  const [testData, setTestData] = useState<TestData | null>(null);
  const [historyRuleSetName, setHistoryRuleSetName] = useState<string | null>(null);
  const [isLoadingHistoryFile, setIsLoadingHistoryFile] = useState(false);

  // AI 動態生成規則
  const [useAiGeneratedRules, setUseAiGeneratedRules] = useState(false);

  // 進度條
  const [reviewProgress, setReviewProgress] = useState(0);
  const [targetProgress, setTargetProgress] = useState(0);
  const [reviewStage, setReviewStage] = useState('');
  const [connectionWarning, setConnectionWarning] = useState(false);

  // 平滑動畫 + 緩慢推進
  const targetRef = useRef(targetProgress);
  targetRef.current = targetProgress;
  const isReviewingRef = useRef(isReviewing);
  isReviewingRef.current = isReviewing;
  const creepCountRef = useRef(0);
  useEffect(() => {
    if (!isReviewing) return;
    creepCountRef.current = 0;
    const timer = setInterval(() => {
      setReviewProgress(prev => {
        if (prev >= 99) return prev;
        const target = targetRef.current;
        if (prev < target) {
          creepCountRef.current = 0;
          return prev + 1;
        }
        const creepThreshold = prev < 80 ? 10 : prev < 90 ? 20 : prev < 95 ? 40 : 75;
        creepCountRef.current += 1;
        if (creepCountRef.current >= creepThreshold) {
          creepCountRef.current = 0;
          return prev + 1;
        }
        return prev;
      });
    }, 200);
    return () => clearInterval(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isReviewing, targetProgress]);

  // 規則集
  const [ruleSetId, setRuleSetId] = useState<number | null>(null);
  const [ruleSets, setRuleSets] = useState<RuleSet[]>([]);
  const [isLoadingRules, setIsLoadingRules] = useState(false);
  const [ruleOptions, setRuleOptions] = useState<{ id: string; name: string }[]>([]);

  // 規則管理 Modal
  const [uploadRuleModalVisible, setUploadRuleModalVisible] = useState(false);
  const [shareModalVisible, setShareModalVisible] = useState(false);
  const [selectedRuleForShare, setSelectedRuleForShare] = useState<RuleSet | null>(null);

  const [previewModalVisible, setPreviewModalVisible] = useState(false);
  const [selectedRuleForPreview, setSelectedRuleForPreview] = useState<RuleSet | null>(null);
  const [manageRuleSets, setManageRuleSets] = useState<RuleSet[] | null>(null);
  const [validationModalVisible, setValidationModalVisible] = useState(false);
  const [reviewHistoryId, setReviewHistoryId] = useState<number | null>(null);

  // -----------------------------
  // 權限
  // -----------------------------
  const activeResults = useMemo(
    () =>
      reviewResults
        .filter(item => !deletedItemIds.has(item._uid))
        .map(item => {
          const overrides = editedItems.get(item._uid);
          return overrides ? { ...item, ...overrides } : item;
        }),
    [reviewResults, deletedItemIds, editedItems]
  );

  // 將當前 activeResults 同步回後端 user_final_analyses（fire-and-forget）
  const syncUserFinalAnalyses = (currentResults: ReviewItem[]) => {
    if (!reviewHistoryId) return;
    const token = authService.getToken();
    const payload = currentResults.map(item => ({
      item_id: item.item_id,
      page: item.page,
      line: item.line,
      rule: item.rule,
      origin: item.origin,
      suggestion: item.suggestion,
      risk_level: item.risk_level,
    }));
    fetch(`${getApiBaseUrl()}/api/review/${reviewHistoryId}/user-analyses`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify({ user_final_analyses: payload }),
    }).catch(() => {}); // 後端失敗不阻斷前端操作
  };

  // 計算套用指定 deleted/edited 狀態後的結果（不依賴 useMemo，立即計算）
  const computeActiveResults = (
    base: ReviewItem[],
    deleted: Set<string>,
    edited: Map<string, Partial<ReviewItem>>
  ): ReviewItem[] =>
    base
      .filter(item => !deleted.has(item._uid))
      .map(item => {
        const overrides = edited.get(item._uid);
        return overrides ? { ...item, ...overrides } : item;
      });

  const handleDeleteCard = (uid: string) => {
    // 盡力記錄到後端（只在有 reviewHistoryId 時）
    const card = reviewResults.find(item => item._uid === uid);
    if (card && reviewHistoryId) {
      const token = authService.getToken();
      fetch(`${getApiBaseUrl()}/api/review/${reviewHistoryId}/cards/${uid}`, {
        method: 'DELETE',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ before_data: card }),
      }).catch(() => {}); // 後端失敗不阻斷前端操作
    }

    // 前端 state
    const newDeletedIds = new Set(deletedItemIds);
    newDeletedIds.add(uid);
    setDeletedItemIds(newDeletedIds);

    // 同步最終結果至後端
    syncUserFinalAnalyses(computeActiveResults(reviewResults, newDeletedIds, editedItems));
  };

  const handleEditCard = (uid: string, changes: Partial<ReviewItem>) => {
    const card = reviewResults.find(item => item._uid === uid);
    if (card && reviewHistoryId) {
      const before = { ...card, ...(editedItems.get(uid) ?? {}) };
      const after = { ...before, ...changes };
      const token = authService.getToken();
      fetch(`${getApiBaseUrl()}/api/review/${reviewHistoryId}/cards/${uid}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ before_data: before, after_data: after }),
      }).catch(() => {}); // 後端失敗不阻斷前端操作
    }

    const newEditedItems = new Map(editedItems);
    newEditedItems.set(uid, { ...(editedItems.get(uid) ?? {}), ...changes });
    setEditedItems(newEditedItems);

    // 同步最終結果至後端
    syncUserFinalAnalyses(computeActiveResults(reviewResults, deletedItemIds, newEditedItems));
  };

  const handleRestoreCard = (uid: string) => {
    const newEditedItems = new Map(editedItems);
    newEditedItems.delete(uid);
    setEditedItems(newEditedItems);

    // 同步最終結果至後端
    syncUserFinalAnalyses(computeActiveResults(reviewResults, deletedItemIds, newEditedItems));
  };

  const selectRuleValue = useAiGeneratedRules ? ('ai_generated' as const) : ruleSetId;

  const handleRuleSelectChange = (val: string | number | null | undefined) => {
    if (val === 'ai_generated') {
      setUseAiGeneratedRules(true);
      setRuleSetId(null);
    } else {
      setUseAiGeneratedRules(false);
      setRuleSetId((val as number | null) ?? null);
    }
  };

  const isSuperAdmin = useMemo(() => !!user?.roles?.includes('SUPER_ADMIN'), [user]);
  const isAdmin = useMemo(
    () => !!(user?.roles?.includes('ADMIN') || isSuperAdmin),
    [user, isSuperAdmin]
  );
  const canUploadRules = useMemo(
    () => isAdmin || !!user?.permissions?.can_upload_rules,
    [isAdmin, user]
  );
  const canShareRules = useMemo(
    () => isAdmin || !!user?.permissions?.can_upload_rules,
    [isAdmin, user]
  );

  const { canUseAiRules, canUploadAiRuleset, legaltechLibraryEnabled } = useFeatureFlags();

  const is401 = (e: any) => e?.response?.status === 401;

  // -----------------------------
  // 規則集：載入
  // -----------------------------
  const loadRuleSets = async () => {
    setIsLoadingRules(true);
    try {
      const [rules, manage] = await Promise.all([loadRuleSetsService(), loadManageRuleSets()]);
      setRuleSets(rules);
      setManageRuleSets(manage);
      // 個人偏好 auto pre-select：若使用者有設偏好且尚未選取規則，自動選好
      // 若無個人偏好，改用公司預設規則集
      const preferredId = user?.preferred_rule_set_id;
      if (preferredId === -1) {
        // AI 自動審閱偏好
        setUseAiGeneratedRules(prev => (prev ? prev : true));
      } else if (preferredId && rules.some(r => r.id === preferredId)) {
        setRuleSetId(prev => (prev === null ? preferredId : prev));
      } else {
        // 無個人偏好，改用公司預設規則集
        const companyDefault = rules.find(r => r.is_default);
        if (companyDefault) {
          setRuleSetId(prev => (prev === null ? companyDefault.id : prev));
        }
      }
    } finally {
      setIsLoadingRules(false);
    }
  };

  useEffect(() => {
    loadRuleSets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!ruleSetId) {
      setRuleOptions([]);
      return;
    }
    loadRuleSetDetail(ruleSetId).then(rows => {
      setRuleOptions(rows.map(r => ({ id: r['序號'], name: r['規則名稱'] })));
    });
  }, [ruleSetId]);

  // -----------------------------
  // 取消審閱並重置 UI（統一入口）
  // -----------------------------
  const currentJobIdRef = useRef<string | null>(null);
  const pollingJobIdRef = useRef<string | null>(null);

  const cancelAndReset = () => {
    pollingJobIdRef.current = null;
    const jobId = currentJobIdRef.current;
    if (jobId) {
      apiClient.post(`${API_ENDPOINTS.reviewCancel}/${jobId}`).catch(() => {});
      currentJobIdRef.current = null;
    }
    setIsReviewing(false);
    setUploadedFile(null);
    setPdfData(null);
    setReviewResults([]);
    setDeletedItemIds(new Set());
    setEditedItems(new Map());
    setTestMode(false);
    setTestData(null);
    setHistoryRuleSetName(null);
    setReviewProgress(0);
    setTargetProgress(0);
    setReviewStage('');
    setConnectionWarning(false);
  };

  // Header「全文審閱」點擊時（已在此頁），透過 location.state.resetKey 觸發重置
  useEffect(() => {
    if (location.state?.resetKey) {
      cancelAndReset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state?.resetKey]);

  // 路由離開（component unmount）→ 僅通知後端取消正在進行的 job
  // 不清除前端 state（切換分頁不觸發此處，state 會保留）
  useEffect(() => {
    return () => {
      pollingJobIdRef.current = null;
      const jobId = currentJobIdRef.current;
      if (jobId) {
        apiClient.post(`${API_ENDPOINTS.reviewCancel}/${jobId}`).catch(() => {});
        currentJobIdRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // -----------------------------
  // 上傳前先確認審閱規則（和泰需求：同仁送審廣宣時常忘記切換規則，預設多為新聞稿規則）
  // 使用者按「繼續」才進入預覽/審閱；按「取消」中止上傳，先改規則
  // -----------------------------
  // 純提示性質(和泰指定文案與排版):單一「確定」按鈕,文字與按鈕皆置中(置中樣式見 App.css 的 .rule-reminder-modal)
  const confirmRuleBeforeUpload = (): Promise<void> => {
    return new Promise(resolve => {
      Modal.warning({
        className: 'rule-reminder-modal',
        icon: null, // 取消預設左側圖示,⚠️ 併入標題以利整體置中
        // 字型大小在此調整：標題 17px、內文 15px（antd 預設為 16/14）
        title: createElement(
          'span',
          { style: { fontSize: 17, fontWeight: 600 } },
          '⚠️ 提醒您檢查審閱規則'
        ),
        content: createElement(
          'div',
          { style: { fontSize: 15, lineHeight: 1.8 } },
          createElement('div', null, '目前系統預設為【新聞稿審閱規則】'),
          createElement('div', null, '若本次送審文件非新聞稿，'),
          createElement('div', null, '請改選【廣宣審閱規則】。')
        ),
        okText: '確定',
        centered: true,
        onOk: () => resolve(),
        // 保險:ESC/其他方式關閉視窗也視同已讀,繼續流程(避免上傳卡住)
        onCancel: () => resolve(),
      });
    });
  };

  // -----------------------------
  // 全文審閱頁專用：上傳前先確認規則，再交給 handleFileUpload
  // （合約比對頁共用 handleFileUpload，不應出現此確認視窗，故以包裝函數區隔）
  // -----------------------------
  const handleReviewFileUpload: NonNullable<UploadProps['beforeUpload']> = async (
    file,
    fileList
  ) => {
    const name = file.name.toLowerCase();
    const validType = name.endsWith('.pdf') || name.endsWith('.docx');
    const validSize = file.size <= 10 * 1024 * 1024;
    if (validType && validSize) {
      await confirmRuleBeforeUpload();
    }
    // 無效檔案直接交由 handleFileUpload 顯示對應錯誤訊息
    return handleFileUpload(file, fileList);
  };

  // -----------------------------
  // 檔案上傳：PDF 直接讀；DOCX 走 preview API 轉 PDF
  // -----------------------------
  const handleFileUpload: NonNullable<UploadProps['beforeUpload']> = async file => {
    if (file.size > 10 * 1024 * 1024) {
      message.error('檔案大小不能超過 10MB');
      return Upload.LIST_IGNORE;
    }

    const fileName = file.name.toLowerCase();
    const isPDF = fileName.endsWith('.pdf');
    const isDOCX = fileName.endsWith('.docx');

    if (!isPDF && !isDOCX) {
      message.error('僅支援 PDF 和 Word (.docx) 格式');
      return Upload.LIST_IGNORE;
    }

    if (isPDF) {
      const reader = new FileReader();
      reader.onload = e => {
        const arrayBuffer = e.target?.result as ArrayBuffer;
        setPdfData(arrayBuffer);
        setUploadedFile(file as unknown as File);
        setReviewResults([]);
        setDeletedItemIds(new Set());
        setEditedItems(new Map());
        setTestMode(false);
        setTestData(null);
      };
      reader.onerror = () => message.error('檔案讀取失敗');
      reader.readAsArrayBuffer(file as unknown as Blob);
      return false;
    }

    // DOCX：呼叫後端 preview 轉 PDF
    try {
      message.loading({ content: '正在轉換 Word 文件...', key: 'docx-preview', duration: 0 });

      const token = authService.getToken();
      if (!token) {
        message.error({ content: '請先登入才能預覽 Word 文件', key: 'docx-preview', duration: 2 });
        window.location.href = '/login';
        return Upload.LIST_IGNORE;
      }

      const formData = new FormData();
      formData.append('file', file as unknown as Blob);

      const resp = await apiClient.post('/api/review/preview-file', formData, {
        responseType: 'blob',
      });

      const pdfBlob = resp.data as Blob;
      const arrayBuffer = await pdfBlob.arrayBuffer();

      message.success({ content: 'Word 文件已轉換為 PDF', key: 'docx-preview', duration: 2 });
      setPdfData(arrayBuffer);
      setUploadedFile(file as unknown as File);
      setReviewResults([]);
      setDeletedItemIds(new Set());
      setEditedItems(new Map());
      setTestMode(false);
      setTestData(null);

      return false;
    } catch (error: any) {
      console.error('DOCX preview failed:', error);
      if (is401(error)) return Upload.LIST_IGNORE;
      message.error({ content: 'Word 文件轉換失敗', key: 'docx-preview', duration: 3 });
      return Upload.LIST_IGNORE;
    }
  };

  // -----------------------------
  // 審閱：submit + polling
  // -----------------------------
  const POLL_INTERVAL = 3000;
  const POLL_TIMEOUT = 600000;

  const STAGE_LABELS: Record<string, string> = {
    pending: '準備中...',
    uploading: '上傳文件中...',
    rule_generation: 'AI 分析文件，生成審閱規則...',
    stage1: 'AI 審閱中...',
    notes: '整理審閱結果...',
    stage2: '檢查註釋...',
    completed: '審查完成！',
    failed: '審查失敗',
  };

  const CONSECUTIVE_FAIL_THRESHOLD = 5;
  const CONSECUTIVE_FAIL_LIMIT = 10;

  const pollJobStatus = async (jobId: string): Promise<void> => {
    pollingJobIdRef.current = jobId;

    const startTime = Date.now();
    let consecutiveFailures = 0;

    while (true) {
      if (pollingJobIdRef.current !== jobId) return;

      if (Date.now() - startTime > POLL_TIMEOUT) {
        throw new Error('審閱超時，請重新嘗試');
      }

      await new Promise(r => setTimeout(r, POLL_INTERVAL));

      if (pollingJobIdRef.current !== jobId) return;

      try {
        const resp = await apiClient.get(`${API_ENDPOINTS.reviewStatus}/${jobId}`);
        const job = resp.data;

        consecutiveFailures = 0;
        setConnectionWarning(false);

        const newPct = job.progress_pct || 0;
        setTargetProgress(prev => Math.max(prev, newPct));
        setReviewStage(STAGE_LABELS[job.status] || '處理中...');

        if (job.status === 'completed') {
          const results = (job.items || []).map((item: ReviewItem, idx: number) => ({
            ...item,
            _uid: `${item.item_id}-${idx}`,
          }));

          setReviewResults(results);
          setDeletedItemIds(new Set());
          setEditedItems(new Map());
          setTargetProgress(100);
          setReviewProgress(100);
          setReviewStage('審查完成！');
          currentJobIdRef.current = null;

          const elapsed = job.elapsed_seconds;
          const timeStr = elapsed
            ? (() => {
                const m = Math.floor(elapsed / 60);
                const s = Math.round(elapsed % 60);
                return m > 0 ? `${m}分${s}秒` : `${s}秒`;
              })()
            : '';

          if (results.length === 0) {
            Modal.success({
              title: '審閱完成 — 文件完全合規！',
              content:
                `本次 AI 審閱未發現任何需修改之處，文件符合所有審閱規則要求。` +
                (timeStr ? `（耗時 ${timeStr}）` : ''),
              okText: '確認',
              centered: true,
            });
          } else {
            message.success(
              `審查完成，找到 ${results.length} 個審查項目` + (timeStr ? `（耗時 ${timeStr}）` : '')
            );
          }
          notifyReviewComplete(results.length, uploadedFile?.name || '');
          if (job.ai_rule_generation_failed) {
            message.warning('AI 規則生成失敗，本次審閱已改用預設規則', 8);
          }
          return;
        }

        if (job.status === 'failed') {
          currentJobIdRef.current = null;
          throw new Error(job.error || '審查失敗');
        }
      } catch (e: any) {
        if (is401(e)) return;
        if (e?.response?.status === 404) {
          currentJobIdRef.current = null;
          throw new Error('審閱任務已失效，請重新審閱');
        }
        consecutiveFailures += 1;
        if (consecutiveFailures >= CONSECUTIVE_FAIL_LIMIT) {
          currentJobIdRef.current = null;
          throw new Error('網路連線中斷，請檢查網路後重新審閱');
        }
        if (consecutiveFailures >= CONSECUTIVE_FAIL_THRESHOLD) {
          setConnectionWarning(true);
        }
        console.warn(
          `Polling error (${consecutiveFailures}/${CONSECUTIVE_FAIL_LIMIT}):`,
          e?.message
        );
      }
    }
  };

  const handleReview = async () => {
    if (!uploadedFile) {
      message.error('請先上傳文件');
      return;
    }

    const token = authService.getToken();
    if (!token) {
      message.error('請先登入');
      window.location.href = '/login';
      return;
    }

    setHistoryRuleSetName(null);
    setIsReviewing(true);
    setReviewProgress(0);
    setTargetProgress(0);
    setReviewStage('準備開始審閱...');
    requestNotificationPermission();

    try {
      const formData = new FormData();
      formData.append('file', uploadedFile);

      const params = new URLSearchParams();
      if (useAiGeneratedRules) params.set('use_ai_generated_rules', 'true');
      else if (ruleSetId) params.set('rule_set_id', String(ruleSetId));
      const queryStr = params.toString();
      const url = queryStr
        ? `${API_ENDPOINTS.reviewSubmit}?${queryStr}`
        : API_ENDPOINTS.reviewSubmit;

      const submitResp = await apiClient.post(url, formData, {
        headers: { Accept: 'application/json' },
      });

      const { job_id } = submitResp.data;
      if (!job_id) throw new Error('未取得 job_id');

      currentJobIdRef.current = job_id;
      setTargetProgress(2);
      setReviewStage('已提交，等待處理...');

      await pollJobStatus(job_id);
    } catch (error: any) {
      console.error('❌ 審查失敗:', error);
      if (is401(error)) return;
      message.error(error?.message || '未知錯誤');
    } finally {
      setIsReviewing(false);
    }
  };

  // -----------------------------
  // 保存審閱記錄（寫入 DB）
  // -----------------------------
  const handleSaveReview = async () => {
    if (reviewResults.length === 0) {
      message.warning('請先完成審查才能保存');
      return;
    }

    const token = authService.getToken();
    if (!token) {
      message.error('請先登入才能保存審查記錄');
      window.location.href = '/login';
      return;
    }

    try {
      const fileName = uploadedFile?.name ?? testData?.fileName ?? '未知檔案';
      let extractedText = reviewResults.map(item => item.origin).join('\n\n');
      if (!extractedText) extractedText = `PDF 檔案: ${fileName}`;

      const riskCounts = calculateRiskCounts(activeResults);

      const aiAnalyses = reviewResults.map(item => ({
        item_id: item.item_id,
        page: item.page,
        line: item.line,
        rule: item.rule,
        origin: item.origin,
        suggestion: item.suggestion,
        risk_level: item.risk_level,
      }));

      const userFinalAnalyses = activeResults.map(item => ({
        item_id: item.item_id,
        page: item.page,
        line: item.line,
        rule: item.rule,
        origin: item.origin,
        suggestion: item.suggestion,
        risk_level: item.risk_level,
      }));

      let fileDataBase64: string | undefined;
      if (uploadedFile) {
        try {
          fileDataBase64 = await fileToBase64(uploadedFile);
        } catch (e) {
          console.warn('⚠️ 檔案編碼失敗，將只保存文字內容:', e);
        }
      }

      const isComparison = location.pathname.includes('contract-comparison');
      const requestData: Parameters<typeof saveReviewHistory>[0] = {
        original_filename: fileName,
        file_type: uploadedFile?.type ?? 'application/pdf',
        file_size: uploadedFile?.size ?? 0,
        file_content: extractedText,
        ai_analyses: aiAnalyses,
        user_final_analyses: userFinalAnalyses,
        risk_summary: riskCounts,
        title: `${fileName} - ${isComparison ? '比對報告' : '審閱報告'}`,
        record_type: isComparison ? 'comparison' : 'review',
      };

      if (useAiGeneratedRules) {
        requestData.rule_set_name = 'AI 自動生成規則';
      } else if (ruleSetId) {
        requestData.rule_set_id = ruleSetId;
        requestData.rule_set_name = ruleSets.find(r => r.id === ruleSetId)?.rule_set_name ?? null;
      }

      if (fileDataBase64) requestData.file_data_base64 = fileDataBase64;

      const response = await saveReviewHistory(requestData);
      console.log('✅ 審查記錄已儲存到後端資料庫，ID:', response.record_id);
      setReviewHistoryId(response.record_id);
      message.success('審查記錄已保存到資料庫，可在歷史記錄中查看和分享');
    } catch (error: any) {
      console.error('❌ 儲存到後端失敗:', error);
      message.error(
        '保存記錄失敗: ' + (error?.response?.data?.detail || error?.message || '未知錯誤')
      );
    }
  };

  // -----------------------------
  // 歷史：載入詳情（含下載原始檔案與 DOCX 轉換）
  // -----------------------------
  const loadHistoryDetail = async (detail: ReviewHistoryDetail) => {
    try {
      const sourceAnalyses = detail.user_final_analyses || detail.ai_analyses;
      const results: ReviewItem[] = sourceAnalyses.map((item, idx) => ({
        item_id: item.item_id,
        _uid: `${item.item_id}-${idx}`,
        page: item.page,
        line: item.line,
        rule: item.rule,
        origin: item.origin,
        suggestion: item.suggestion,
        risk_level: item.risk_level,
      }));

      setReviewResults(results);
      setDeletedItemIds(new Set());
      setEditedItems(new Map());
      setReviewHistoryId(detail.id); // 讓後續卡片編輯/刪除能正確寫入 review_card_operations
      setTestMode(true);
      setTestData({
        fileName: detail.original_filename,
        timestamp: new Date(detail.created_at).getTime(),
      });
      // 嘗試在下拉選單中還原歷史使用的規則集；若已無存取權則退而顯示文字 tag
      if (detail.rule_set_id && ruleSets.some(r => r.id === detail.rule_set_id)) {
        // 規則集仍可使用 → 直接選回
        setRuleSetId(detail.rule_set_id);
        setUseAiGeneratedRules(false);
        setHistoryRuleSetName(null);
      } else if (
        !detail.rule_set_id &&
        detail.rule_set_name === 'AI 自動生成規則' &&
        canUseAiRules
      ) {
        // AI 自動審閱且使用者仍有權限 → 切回 AI 模式
        setUseAiGeneratedRules(true);
        setRuleSetId(null);
        setHistoryRuleSetName(null);
      } else {
        // 規則集已不可用 → 顯示文字 tag
        setHistoryRuleSetName(detail.rule_set_name ?? null);
      }

      if (!detail.has_file_data) {
        message.info('此記錄沒有原始檔案，僅顯示審查結果。如需查看原始文件，請重新上傳。');
        setPdfData(null);
        setUploadedFile(null);
        return;
      }

      const token = authService.getToken();
      if (!token) {
        message.error('請先登入才能下載歷史原始檔案');
        window.location.href = '/login';
        return;
      }

      setIsLoadingHistoryFile(true);
      try {
        const downloadResp = await apiClient.get(`/api/v1/review-history/${detail.id}/download`, {
          responseType: 'blob',
        });

        const blob = downloadResp.data as Blob;
        const fileNameLower = detail.original_filename.toLowerCase();
        const isDocx = fileNameLower.endsWith('.docx');

        const file = new File([blob], detail.original_filename, {
          type: detail.file_type || 'application/pdf',
        });
        setUploadedFile(file);

        if (isDocx) {
          const formData = new FormData();
          formData.append('file', file);

          const previewResp = await apiClient.post('/api/review/preview-file', formData, {
            responseType: 'blob',
          });

          const pdfBlob = previewResp.data as Blob;
          setPdfData(await pdfBlob.arrayBuffer());
        } else {
          setPdfData(await blob.arrayBuffer());
        }

        message.success(`已載入歷史記錄: ${detail.title || detail.original_filename}`);
      } finally {
        setIsLoadingHistoryFile(false);
      }
    } catch (e: any) {
      console.error('❌ 載入歷史記錄失敗:', e);
      if (is401(e)) return;
      message.error('載入歷史記錄失敗');
    }
  };

  const showHistory = () => setHistoryVisible(true);

  const exitTestMode = () => {
    setTestMode(false);
    setTestData(null);
    setReviewResults([]);
    setDeletedItemIds(new Set());
    setEditedItems(new Map());
    setPdfData(null);
    setUploadedFile(null);
    message.info('已退出測試模式');
  };

  // -----------------------------
  // 下載 Word 報告（附評論）
  // -----------------------------
  const handleDownloadWordReport = async () => {
    if (reviewResults.length === 0) {
      message.warning('沒有審查結果，無法生成 Word 報告');
      return;
    }

    if (!uploadedFile) {
      message.warning('下載 Word 報告需要原始檔案，請重新上傳後再試');
      return;
    }

    const token = authService.getToken();
    if (!token) {
      message.error('請先登入才能下載 Word 報告');
      window.location.href = '/login';
      return;
    }

    const isPDF = uploadedFile.name.toLowerCase().endsWith('.pdf');
    const expectedTime = isPDF ? '30-60 秒' : '10-20 秒';
    message.info(
      `正在${isPDF ? 'PDF 轉換並' : ''}生成附評論的 Word 報告，預計需要 ${expectedTime}...`
    );
    const loadingMsg = message.loading('處理中，請稍候...', 0);

    try {
      const formData = new FormData();
      formData.append('file', uploadedFile);
      const resultsForBackend = activeResults.map(({ _uid, ...rest }) => rest);
      formData.append('results', JSON.stringify(resultsForBackend));
      if (isPDF) formData.append('pdf_strategy', 'smart');

      const resp = await apiClient.post('/api/review/download-commented-word', formData, {
        responseType: 'blob',
      });

      const blob = resp.data as Blob;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;

      const originalName = uploadedFile.name.replace(/\.(pdf|docx)$/i, '');
      a.download = `reviewed_${originalName}.docx`;

      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      message.success('Word 報告已生成並開始下載！');
    } catch (e: any) {
      console.error('下載失敗:', e);
      if (is401(e)) return;
      message.error(`下載失敗: ${e.message || '未知錯誤'}`);
    } finally {
      loadingMsg();
    }
  };

  // -----------------------------
  // UI actions：開關 modal / 重置
  // -----------------------------
  const openUploadRuleModal = () => setUploadRuleModalVisible(true);
  const closeUploadRuleModal = () => setUploadRuleModalVisible(false);

  const openShareModal = (ruleSet: RuleSet) => {
    setSelectedRuleForShare(ruleSet);
    setShareModalVisible(true);
  };
  const closeShareModal = () => {
    setShareModalVisible(false);
    setSelectedRuleForShare(null);
  };

  const openPreviewModal = (ruleSet: RuleSet) => {
    setSelectedRuleForPreview(ruleSet);
    setPreviewModalVisible(true);
  };
  const closePreviewModal = () => {
    setPreviewModalVisible(false);
    setSelectedRuleForPreview(null);
  };

  const handleRuleSetNameChange = (id: number, newName: string) => {
    const update = (list: RuleSet[]) =>
      list.map(r => (r.id === id ? { ...r, rule_set_name: newName } : r));
    setRuleSets(prev => update(prev));
    setManageRuleSets(prev => (prev ? update(prev) : prev));
  };

  const resetUpload = () => {
    cancelAndReset();
    message.info('已清除文件，請重新上傳');
  };

  return {
    // data
    user,

    uploadedFile,
    pdfData,
    reviewResults,
    activeResults,
    isReviewing,

    historyVisible,
    testMode,
    testData,
    historyRuleSetName,
    isLoadingHistoryFile,

    useAiGeneratedRules,
    selectRuleValue,
    handleRuleSelectChange,

    reviewProgress,
    reviewStage,
    connectionWarning,

    ruleSetId,
    ruleSets: legaltechLibraryEnabled ? ruleSets : ruleSets.filter(r => !r.is_legaltech_library),
    ruleOptions,
    isLoadingRules,
    manageRuleSets,

    // permissions
    isSuperAdmin,
    isAdmin,
    canUploadRules,
    canShareRules,
    canUseAiRules,
    canUploadAiRuleset,

    // ui state (modals)
    uploadRuleModalVisible,
    shareModalVisible,
    previewModalVisible,
    selectedRuleForShare,
    selectedRuleForPreview,

    // setters / actions
    setRuleSetId,
    setHistoryVisible,

    loadRuleSets,
    showHistory,
    exitTestMode,

    handleFileUpload,
    handleReviewFileUpload,
    handleReview,
    handleSaveReview,
    handleDownloadWordReport,
    handleValidationReport: () => setValidationModalVisible(true),
    validationModalVisible,
    closeValidationModal: () => setValidationModalVisible(false),
    handleDeleteCard,
    editedItems,
    handleEditCard,
    handleRestoreCard,
    handleSetUserPreferred: async (ruleSetId: number | null) => {
      const result = await setUserPreferredRule(ruleSetId);
      if (result.success) {
        await refreshUser();
        if (ruleSetId !== null) {
          message.success('個人預設規則已設定');
        } else {
          message.success('個人預設規則已清除');
        }
      } else {
        message.error(`設定失敗: ${result.error}`);
      }
    },

    handleValidationModeToggle: async (value: boolean) => {
      const token = authService.getToken();
      if (!token) return;
      try {
        const res = await fetch(`${getApiBaseUrl()}/api/v1/user/me/validation-mode`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ validation_mode: value }),
        });
        if (res.ok) await refreshUser();
        else message.error('驗測模式切換失敗');
      } catch {
        message.error('驗測模式切換失敗');
      }
    },
    loadHistoryDetail,

    openUploadRuleModal,
    closeUploadRuleModal,
    openShareModal,
    closeShareModal,
    openPreviewModal,
    closePreviewModal,
    handleRuleSetNameChange,

    resetUpload,
  };
};
